import React, { useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditLocationIcon from "@mui/icons-material/EditLocation";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import PlaceIcon from "@mui/icons-material/Place";
import {
  type Control,
  Controller,
  type UseFormRegister,
  type UseFormReturn,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { SelectLocationModal } from "@/components/AcquireBook/SelectLocationModal";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET } from "@/styles/responsive";
import { type PropertyLocationRow } from "@/types/propertyLocationRow";
import { createEmptyPropertyRow } from "@/utils/propertyRows";
import { PREFECTURES } from "@/utils/prefecture";
import { type ResearchRequestFormData } from "../types";
import { CLabeledCheckbox } from "./CLabeledCheckbox";
import { FieldLabel, HelpText, inputSx, SectionCard } from "./formLayout";

interface PropertyInputSectionProps {
  form: UseFormReturn<ResearchRequestFormData>;
}

type CandidateFieldName =
  | `chibanList.${number}.prefName`
  | `kaokuNumberList.${number}.prefName`;

interface CCandidateAutocompleteProps {
  control: Control<ResearchRequestFormData>;
  name: CandidateFieldName;
  label: string;
  placeholder: string;
  options: string[];
  disabled?: boolean;
}

/** 対象住所から作った候補を選べる、自由入力の欄。 */
const CCandidateAutocomplete: React.FC<CCandidateAutocompleteProps> = ({
  control,
  name,
  label,
  placeholder,
  options,
  disabled = false,
}) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <Autocomplete
        freeSolo
        forcePopupIcon
        disabled={disabled}
        options={options}
        value={field.value}
        onBlur={field.onBlur}
        onChange={(_event, value) => {
          field.onChange(value ?? "");
        }}
        onInputChange={(_event, value, reason) => {
          if (reason === "input") field.onChange(value);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={field.ref}
            inputProps={{ ...params.inputProps, "aria-label": label }}
            placeholder={placeholder}
            size="small"
            fullWidth
            sx={inputSx}
          />
        )}
      />
    )}
  />
);

// SP で押しやすいよう、削除ボタンは 44px 四方を確保する
const tapTargetSx = { minWidth: MIN_TAP_TARGET, minHeight: MIN_TAP_TARGET };

// 地番と家屋番号は、リアシストと同じく見出しの色で見分ける
const propertyRowsKinds = {
  chiban: {
    listName: "chibanList",
    numberLabel: "地番",
    headerColor: "primary.main",
  },
  kaoku: {
    listName: "kaokuNumberList",
    numberLabel: "家屋番号",
    headerColor: "secondary.main",
  },
} as const;

type PropertyRowsKind = keyof typeof propertyRowsKinds;

interface LocationTarget {
  kind: PropertyRowsKind;
  index: number;
}

interface CPropertyRowsFieldProps {
  kind: PropertyRowsKind;
  control: Control<ResearchRequestFormData>;
  register: UseFormRegister<ResearchRequestFormData>;
  rows: {
    fields: Array<{ id: string }>;
    append: (row: PropertyLocationRow) => void;
    remove: (index: number) => void;
  };
  prefectureOptions: string[];
  /** 所在選択ダイアログを開く。都道府県が未選択のときは undefined */
  onSelectLocation?: (index: number) => void;
  /** 真のとき、行を入力できなくする（家屋番号が該当なしのとき） */
  disabled?: boolean;
  action?: React.ReactNode;
  errorMessage?: string;
}

/**
 * 地番・家屋番号の入力行の一覧（リアシストの対象地番の表と同じ作り）。
 * PC は表、SP は横スクロールを出さないよう1行1カードで出す。
 */
const CPropertyRowsField: React.FC<CPropertyRowsFieldProps> = ({
  kind,
  control,
  register,
  rows,
  prefectureOptions,
  onSelectLocation,
  disabled = false,
  action,
  errorMessage,
}) => {
  const isMobile = useIsMobile();
  const { listName, numberLabel, headerColor } = propertyRowsKinds[kind];

  const renderInputs = (
    index: number
  ): Record<
    "prefName" | "location" | "number" | "remove",
    React.ReactNode
  > => ({
    prefName: (
      <CCandidateAutocomplete
        control={control}
        name={`${listName}.${index}.prefName`}
        label={`都道府県（${numberLabel}）`}
        placeholder="都道府県"
        options={prefectureOptions}
        disabled={disabled}
      />
    ),
    // 所在は対象住所と一致するとは限らないため、登記の所在から選ぶほかに自由入力もできる
    location: (
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          {...register(`${listName}.${index}.location`)}
          inputProps={{ "aria-label": `所在（${numberLabel}）` }}
          placeholder="所在"
          size="small"
          fullWidth
          disabled={disabled}
          sx={inputSx}
        />
        <Tooltip
          title={
            onSelectLocation ? "" : "先に対象住所の都道府県を選んでください"
          }
          placement="top"
          arrow
          enterTouchDelay={0}
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              aria-label={`所在（${numberLabel}${index + 1}）を選ぶ`}
              disabled={disabled || !onSelectLocation}
              onClick={() => {
                onSelectLocation?.(index);
              }}
              sx={{ whiteSpace: "nowrap", minHeight: 40 }}
            >
              選ぶ
            </Button>
          </span>
        </Tooltip>
      </Box>
    ),
    number: (
      <TextField
        {...register(`${listName}.${index}.chibanAndKaokuNumber`)}
        inputProps={{ "aria-label": numberLabel }}
        placeholder={numberLabel}
        size="small"
        fullWidth
        disabled={disabled}
        sx={inputSx}
      />
    ),
    remove: (
      <IconButton
        aria-label={`${numberLabel}行${index + 1}を削除`}
        size="small"
        color="error"
        disabled={disabled || rows.fields.length === 1}
        onClick={() => {
          rows.remove(index);
        }}
        sx={{
          ...tapTargetSx,
          backgroundColor: "error.50",
          borderRadius: 2,
          "&:hover": { backgroundColor: "error.100" },
          "&.Mui-disabled": { backgroundColor: "grey.100", color: "grey.400" },
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    ),
  });

  const addButton = (
    <Button
      variant="outlined"
      startIcon={<AddIcon />}
      disabled={disabled}
      onClick={() => {
        rows.append(createEmptyPropertyRow());
      }}
      sx={{
        borderRadius: 3,
        px: 3,
        py: 1.25,
        borderStyle: "dashed",
        borderWidth: 2,
        "&:hover": {
          borderStyle: "solid",
          borderWidth: 2,
          backgroundColor: "primary.50",
        },
      }}
    >
      {numberLabel}行を追加
    </Button>
  );

  const headings: Array<[React.ReactNode, string]> = [
    [<AccountBalanceIcon key="prefName" fontSize="small" />, "都道府県"],
    [<EditLocationIcon key="location" fontSize="small" />, "所在"],
    [<FormatListNumberedIcon key="number" fontSize="small" />, numberLabel],
  ];

  return (
    <SectionCard title={`対象${numberLabel}`} action={action}>
      {isMobile ? (
        <Stack spacing={1.5}>
          {rows.fields.map((row, index) => {
            const inputs = renderInputs(index);
            return (
              <Paper
                key={row.id}
                variant="outlined"
                sx={{ p: 1.5, borderRadius: 3 }}
              >
                <Stack spacing={1.25}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      {numberLabel} {index + 1}
                    </Typography>
                    {inputs.remove}
                  </Box>
                  {/* SP には表の見出しが無いため、入力欄ごとに項目名を出す */}
                  {(
                    [
                      ["都道府県", inputs.prefName],
                      ["所在", inputs.location],
                      [numberLabel, inputs.number],
                    ] as const
                  ).map(([fieldLabel, input]) => (
                    <Box key={fieldLabel}>
                      <FieldLabel>{fieldLabel}</FieldLabel>
                      {input}
                    </Box>
                  ))}
                </Stack>
              </Paper>
            );
          })}
          <Box sx={{ textAlign: "center" }}>{addButton}</Box>
        </Stack>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            boxShadow:
              "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
            overflow: "hidden",
          }}
        >
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "20%" }} />
              <col
                data-testid={`${kind}-location-column`}
                style={{ width: "52%" }}
              />
              <col
                data-testid={`${kind}-number-column`}
                style={{ width: "18%" }}
              />
              <col style={{ width: "10%" }} />
            </colgroup>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: headerColor,
                  // 本番ビルドでは MUI の見出しの既定色（黒）が後から当たるため、詳細度を上げて白にする
                  "&& .MuiTableCell-head": { color: "common.white" },
                }}
              >
                {headings.map(([icon, heading]) => (
                  <TableCell key={heading} sx={{ fontWeight: 600, py: 1.5 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {icon}
                      {heading}
                    </Box>
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 600, py: 1.5 }}>
                  操作
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.fields.map((row, index) => {
                const inputs = renderInputs(index);
                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      "&:nth-of-type(odd)": { backgroundColor: "grey.50" },
                      "&:hover": { backgroundColor: "primary.50" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>{inputs.prefName}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{inputs.location}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{inputs.number}</TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      {inputs.remove}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{
                    py: 2,
                    borderTop: "2px dashed",
                    borderColor: "grey.300",
                  }}
                >
                  {addButton}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {errorMessage ? (
        <Typography
          color="error"
          variant="caption"
          display="block"
          sx={{ mt: 1 }}
        >
          {errorMessage}
        </Typography>
      ) : null}
    </SectionCard>
  );
};

export const PropertyInputSection: React.FC<PropertyInputSectionProps> = ({
  form,
}) => {
  const { control, register, formState, setValue } = form;
  const addressPrefecture = useWatch({ control, name: "addressPrefecture" });
  const noKaokuNumber = useWatch({ control, name: "noKaokuNumber" });
  const [locationTarget, setLocationTarget] = useState<LocationTarget | null>(
    null
  );
  // ダイアログを閉じるたびに進める。閉じた後に遅れて返った選択結果を、別の行に書き込まないため
  const locationSessionRef = useRef(0);
  const locationSession = locationSessionRef.current;
  const prefectureId = PREFECTURES.find(
    (prefecture) => prefecture.name === addressPrefecture
  )?.id;
  const openLocationSelector = (
    kind: PropertyRowsKind
  ): ((index: number) => void) | undefined =>
    prefectureId === undefined
      ? undefined
      : (index) => {
          setLocationTarget({ kind, index });
        };
  const chibanRows = useFieldArray({ control, name: "chibanList" });
  const kaokuRows = useFieldArray({ control, name: "kaokuNumberList" });
  const prefectureOptions = useMemo(
    () => (addressPrefecture ? [addressPrefecture] : []),
    [addressPrefecture]
  );
  const chibanListErrorMessage =
    formState.errors.chibanList?.message ??
    formState.errors.chibanList?.root?.message;
  const kaokuListErrorMessage =
    formState.errors.kaokuNumberList?.message ??
    formState.errors.kaokuNumberList?.root?.message;

  return (
    <Stack component="section" aria-label="物件情報" spacing={2}>
      <SectionCard
        title="対象住所"
        icon={<PlaceIcon sx={{ color: "#1976d2", fontSize: 20 }} />}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "200px 1fr" },
            gap: 1.5,
            alignItems: "start",
          }}
        >
          <Box>
            <FieldLabel required>都道府県</FieldLabel>
            <Controller
              control={control}
              name="addressPrefecture"
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  size="small"
                  fullWidth
                  SelectProps={{
                    displayEmpty: true,
                    SelectDisplayProps: { "aria-label": "住所の都道府県" },
                  }}
                  error={Boolean(formState.errors.addressPrefecture)}
                  helperText={formState.errors.addressPrefecture?.message}
                  sx={inputSx}
                >
                  <MenuItem value="">
                    <Typography component="span" color="text.disabled">
                      選択してください
                    </Typography>
                  </MenuItem>
                  {PREFECTURES.map((prefecture) => (
                    <MenuItem key={prefecture.id} value={prefecture.name}>
                      {prefecture.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>
          <Box>
            <FieldLabel required>対象住所</FieldLabel>
            <TextField
              {...register("address")}
              placeholder="例：渋谷区渋谷1-1-1"
              size="small"
              fullWidth
              inputProps={{ "aria-label": "対象住所" }}
              error={Boolean(formState.errors.address)}
              helperText={formState.errors.address?.message}
              sx={inputSx}
            />
          </Box>
        </Box>
        <Box sx={{ mt: 1.5 }}>
          <CLabeledCheckbox
            control={control}
            name="isIsland"
            label="対象物件は離島に所在する"
          />
          <HelpText>
            離島の場合、簡易調査・詳細調査は個別見積、標準納期は要相談となります。
          </HelpText>
        </Box>
      </SectionCard>

      <CPropertyRowsField
        kind="chiban"
        control={control}
        register={register}
        rows={chibanRows}
        prefectureOptions={prefectureOptions}
        onSelectLocation={openLocationSelector("chiban")}
        errorMessage={chibanListErrorMessage}
      />

      <CPropertyRowsField
        kind="kaoku"
        control={control}
        register={register}
        rows={kaokuRows}
        prefectureOptions={prefectureOptions}
        onSelectLocation={openLocationSelector("kaoku")}
        disabled={noKaokuNumber}
        errorMessage={kaokuListErrorMessage}
        action={
          <Controller
            control={control}
            name="noKaokuNumber"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(event) => {
                      field.onChange(event.target.checked);
                      if (event.target.checked) {
                        kaokuRows.replace([createEmptyPropertyRow()]);
                      }
                    }}
                    sx={tapTargetSx}
                  />
                }
                label="該当なし"
              />
            )}
          />
        }
      />

      {/* 既存の所在選択ダイアログは選択途中の値をコンポーネントの外に持つため、1つだけ置いて対象の行を覚えておく。
          都道府県の一覧を読み終えてから開けるよう、登記取得の画面と同じく常に置いて open で切り替える */}
      {prefectureId !== undefined ? (
        <SelectLocationModal
          open={locationTarget !== null}
          onClose={() => {
            locationSessionRef.current += 1;
            setLocationTarget(null);
          }}
          prefectureId={prefectureId}
          setLocationWithoutCity={(location) => {
            if (
              !locationTarget ||
              locationSession !== locationSessionRef.current
            ) {
              return;
            }
            const { listName } = propertyRowsKinds[locationTarget.kind];
            const rowPath = `${listName}.${locationTarget.index}` as const;
            const options = { shouldDirty: true, shouldValidate: true };
            // ダイアログは対象住所の都道府県で検索するため、行の都道府県もそろえる
            setValue(`${rowPath}.prefName`, addressPrefecture, options);
            setValue(`${rowPath}.location`, location, options);
          }}
        />
      ) : null}
    </Stack>
  );
};
